import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ScanLine, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const READER_ID = "ff-barcode-reader";

export default function BarcodeScanner({ open, onOpenChange, onResult }) {
  const [manual, setManual] = useState("");
  const [looking, setLooking] = useState(false);
  const [camError, setCamError] = useState("");
  const scannerRef = useRef(null);
  const handledRef = useRef(false);

  const doLookup = async (code) => {
    if (looking) return;
    setLooking(true);
    handledRef.current = true;
    try {
      const { data } = await api.get(`/products/lookup/${code}`);
      if (!data.found) {
        toast.info("Produit introuvable — saisissez le nom manuellement.");
        onResult({ found: false, barcode: code });
      } else {
        toast.success(`« ${data.name || "Produit"} » reconnu.`);
        onResult(data);
      }
      onOpenChange(false);
    } catch (err) {
      handledRef.current = false;
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLooking(false);
    }
  };

  // camera lifecycle
  useEffect(() => {
    if (!open) return;
    handledRef.current = false;
    setCamError("");
    let cancelled = false;
    let instance = null;

    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        instance = new Html5Qrcode(READER_ID, { verbose: false });
        scannerRef.current = instance;
        await instance.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            if (handledRef.current) return;
            const code = (decoded || "").replace(/\D/g, "");
            if (code.length >= 6) doLookup(code);
          },
          () => {}
        );
      } catch (e) {
        if (!cancelled) setCamError("Caméra indisponible. Saisissez le code-barres ci-dessous.");
      }
    })();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
      }
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" /> Scanner un code-barres
          </DialogTitle>
          <DialogDescription>Visez le code-barres, ou saisissez-le à la main.</DialogDescription>
        </DialogHeader>

        <div id={READER_ID} className="rounded-2xl overflow-hidden bg-black/80 min-h-[220px] flex items-center justify-center"
          data-testid="barcode-reader">
          {camError && <p className="text-sm text-white/70 px-6 text-center">{camError}</p>}
        </div>

        <div className="flex items-center gap-2">
          <Input
            value={manual}
            onChange={(e) => setManual(e.target.value.replace(/\D/g, ""))}
            placeholder="Ex. 3017620422003"
            inputMode="numeric"
            data-testid="manual-barcode-input"
          />
          <Button
            type="button"
            className="rounded-full shrink-0"
            disabled={manual.length < 6 || looking}
            onClick={() => doLookup(manual)}
            data-testid="manual-barcode-search"
          >
            {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1.5">Rechercher</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
